import api from './axiosInstance';

export const WORKFLOW_STATUS = {
  IN_PROGRESS: 1,
  APPROVED: 2,
  REJECTED: 3,
  FINALIZED: 4,
};

export const NODE_TYPE = {
  TASK: 1,
  APPROVAL: 2,
};

export const APPROVAL_STATUS = {
  PENDING: 1,
  APPROVED: 2,
  REJECTED: 3,
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const BY_REFERENCE_CACHE_TTL_MS = 60 * 1000;
const workflowByReferenceCache = new Map();
const workflowByReferenceInFlight = new Map();

const buildReferenceCacheKey = (referenceType, referenceCode, params = {}) => {
  const safeType = String(referenceType || '').trim().toUpperCase();
  const safeCode = String(referenceCode || '').trim();
  const orderedParams = Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${String(value)}`)
    .join('|');

  return `${safeType}::${safeCode}::${orderedParams}`;
};

const getCachedByReference = (cacheKey) => {
  const cached = workflowByReferenceCache.get(cacheKey);
  if (!cached) return undefined;

  if (cached.expiresAtUtc <= Date.now()) {
    workflowByReferenceCache.delete(cacheKey);
    return undefined;
  }

  return cached.value;
};

const setCachedByReference = (cacheKey, value) => {
  workflowByReferenceCache.set(cacheKey, {
    value,
    expiresAtUtc: Date.now() + BY_REFERENCE_CACHE_TTL_MS,
  });
};

const clearWorkflowReferenceCache = () => {
  workflowByReferenceCache.clear();
  workflowByReferenceInFlight.clear();
};

export const invalidateWorkflowReferenceCache = () => {
  clearWorkflowReferenceCache();
};

const resolveWorkflowErrorMessage = (error, fallbackMessage) => {
  const payload = error?.response?.data;

  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }

  if (payload?.message && String(payload.message).trim()) {
    return String(payload.message).trim();
  }

  if (payload?.title && String(payload.title).trim()) {
    return String(payload.title).trim();
  }

  if (payload?.errors && typeof payload.errors === 'object') {
    const firstField = Object.keys(payload.errors)[0];
    const firstMessage = firstField
      ? payload.errors[firstField]?.[0]
      : null;

    if (firstMessage && String(firstMessage).trim()) {
      return String(firstMessage).trim();
    }
  }

  if (error?.message && String(error.message).trim()) {
    return String(error.message).trim();
  }

  return fallbackMessage;
};

export const getWorkflowDefinitions = async (params = {}) => {
  const response = await api.get('/workflow/definitions', { params });
  return normalizeList(response.data);
};

export const createWorkflowDefinition = async (payload) => {
  try {
    const response = await api.post('/workflow/definitions', payload);
    clearWorkflowReferenceCache();
    return response.data;
  } catch (error) {
    error.customMessage = resolveWorkflowErrorMessage(
      error,
      'No fue posible crear el workflow.'
    );
    throw error;
  }
};

export const updateWorkflowDefinition = async (definitionId, payload) => {
  try {
    const response = await api.put(
      `/workflow/definitions/${definitionId}`,
      payload
    );
    clearWorkflowReferenceCache();
    return response.data;
  } catch (error) {
    error.customMessage = resolveWorkflowErrorMessage(
      error,
      'No fue posible actualizar el workflow.'
    );
    throw error;
  }
};

export const deleteWorkflowDefinition = async (definitionId) => {
  await api.delete(`/workflow/definitions/${definitionId}`);
  clearWorkflowReferenceCache();
};

export const getWorkflowNomenclatures = async () => {
  const response = await api.get('/Task/GetTaskCode');
  return normalizeList(response.data)
    .map((item) => {
      if (typeof item === 'string') {
        const value = item.trim();
        return value ? { label: value, value } : null;
      }

      const raw =
        item?.name ??
        item?.Name ??
        item?.code ??
        item?.Code ??
        item?.nomenclature ??
        item?.Nomenclature ??
        '';

      const value = String(raw || '').trim();
      if (!value) return null;

      return { label: value, value };
    })
    .filter(Boolean);
};

export const getWorkflowInstances = async (params = {}) => {
  const response = await api.get('/workflow/instances', { params });
  return normalizeList(response.data);
};

export const getWorkflowInstanceByReference = async (
  referenceType,
  referenceCode,
  params = {}
) => {
  if (!referenceType || !referenceCode) {
    return null;
  }

  const cacheKey = buildReferenceCacheKey(referenceType, referenceCode, params);
  const cachedValue = getCachedByReference(cacheKey);
  if (cachedValue !== undefined) {
    return cachedValue;
  }

  if (workflowByReferenceInFlight.has(cacheKey)) {
    return workflowByReferenceInFlight.get(cacheKey);
  }

  const lookupPromise = (async () => {
    try {
      const response = await api.get('/workflow/instances/by-reference', {
        params: {
          referenceType,
          referenceCode,
          ...params,
        },
      });

      const result = response.data || null;
      setCachedByReference(cacheKey, result);
      return result;
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.status === 403) {
        return null;
      }

      throw error;
    } finally {
      workflowByReferenceInFlight.delete(cacheKey);
    }
  })();

  workflowByReferenceInFlight.set(cacheKey, lookupPromise);
  return lookupPromise;
};

export const getWorkflowInstance = async (instanceId) => {
  const response = await api.get(`/workflow/instances/${instanceId}`);
  return response.data;
};

export const createWorkflowInstance = async (payload) => {
  const response = await api.post('/workflow/instances', payload);
  clearWorkflowReferenceCache();
  return response.data;
};

export const advanceWorkflowNode = async (
  instanceId,
  instanceNodeId,
  payload
) => {
  const response = await api.post(
    `/workflow/instances/${instanceId}/nodes/${instanceNodeId}/advance`,
    payload
  );
  clearWorkflowReferenceCache();
  return response.data;
};

export const approveWorkflowNode = async (
  instanceId,
  instanceNodeId,
  payload
) => {
  const response = await api.post(
    `/workflow/instances/${instanceId}/nodes/${instanceNodeId}/approve`,
    payload
  );
  clearWorkflowReferenceCache();
  return response.data;
};

export const rejectWorkflowNode = async (
  instanceId,
  instanceNodeId,
  payload
) => {
  const response = await api.post(
    `/workflow/instances/${instanceId}/nodes/${instanceNodeId}/reject`,
    payload
  );
  clearWorkflowReferenceCache();
  return response.data;
};
